/**
 * Changelog Modal
 *
 * Displays the last 100 git commits grouped by date.
 * Matches ai-signal's changelog panel design exactly:
 * centered modal, 780px wide, commits with SHA badges linking to GitHub.
 */

import { useState, useEffect } from 'react';
import { IconButton } from './ui';

interface Commit {
  sha: string;
  short_sha: string;
  message: string;
  author: string;
  date: string;
}

interface ChangelogProps {
  open: boolean;
  onClose: () => void;
}

export function Changelog({ open, onClose }: ChangelogProps) {
  const [commits, setCommits] = useState<Commit[]>([]);
  const [repoUrl, setRepoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetch('/api/changelog')
      .then((res) => res.json())
      .then((data) => {
        setCommits(data.commits || []);
        setRepoUrl(data.repoUrl || null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  // Group commits by date
  const byDate: Record<string, Commit[]> = {};
  for (const c of commits) {
    if (!byDate[c.date]) byDate[c.date] = [];
    byDate[c.date]!.push(c);
  }
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  function formatDate(dateStr: string) {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  return (
    // Backdrop
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Panel */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          width: 780,
          maxWidth: '94vw',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,.35)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>
              Change Log
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Last 100 commits
            </div>
          </div>
          <IconButton
            tone="muted"
            aria-label="Close"
            onClick={onClose}
            style={{ fontSize: 16 }}
          >
            ✕
          </IconButton>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '8px 0 12px', flex: 1 }}>
          {loading && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Loading commits...
            </div>
          )}

          {error && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--red)', fontSize: 13 }}>
              Failed to load changelog.
            </div>
          )}

          {!loading && !error && sortedDates.map((date) => (
            <div key={date} style={{ padding: '16px 24px 4px' }}>
              {/* Date label */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--text-muted)',
                  marginBottom: 8,
                }}
              >
                {formatDate(date)}
              </div>

              {/* Commit entries */}
              {byDate[date]!.map((c) => (
                <div
                  key={c.sha}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: '10px 12px',
                    borderRadius: 8,
                    marginBottom: 2,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* SHA badge */}
                  {repoUrl ? (
                    <a
                      href={`${repoUrl}/commit/${c.sha}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: 'var(--accent)',
                        background: 'var(--accent-glow)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        marginTop: 1,
                        textDecoration: 'none',
                      }}
                    >
                      {c.short_sha}
                    </a>
                  ) : (
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 11,
                        color: 'var(--accent)',
                        background: 'var(--accent-glow)',
                        padding: '2px 6px',
                        borderRadius: 4,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      {c.short_sha}
                    </span>
                  )}

                  {/* Message + author */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.45 }}>
                      {c.message}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                      {c.author}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {!loading && !error && commits.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No commits found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
